import { defineStore } from 'pinia'
import { ref } from 'vue'
import { ThemePreference, usePreferencesStore } from '@/stores/preferences.ts'
import { Capacitor } from '@capacitor/core'
import { StatusBar, Style } from '@capacitor/status-bar'
import { catalogQuery } from '@/data/catalogClient.ts'
import { GET_PUBLISHERS } from '@/queries'
import { Publisher } from '../../__generated__/graphql.ts'
import { publisherNameToCssClass } from '@/utils'

const defaultTheme = {
  color: {
    background: '#161616',
    mainBackground: '#161616',
    secondaryBackground: '#212121',
    transparentBackground: 'rgba(28,28,28,0.9)',
    releaseEntryTransparentOverlay: 'rgba(22,22,22,0.8)',
    modalBackground: '#2D2D2D',
    backdrop: 'rgba(22,22,22,0.8)',
    bg1: '#1E1E1E',
    buttonBg: '#2F2F2F',
    atsumeMain: '#A6015F',
    cardText: '#963232',
    errorColor: '#FF5858',
    dangerButtonBg: '#6B1717',
    rot: '#6B1717',
    rotHeller: '#AF4545',
    rotNochHeller: '#E26565',
    textOnPublisherLabel: '#f3f3f3',
    mainText: '#f3f3f3',
    mainTextSlightlyDarker: '#A7A7A7',
    switchKnobColor: '#D0D2D5',
    switchKnobColorOff: '#2D2D2D',
    mainOnDark: '#f3f3f3',
    mainOnDarkMuted: '#A7A7A7',
    mainOnLight: '#000000',
    mainTextInactive: '#696969',
    notSelected: '#636363',
    grey1: '#B7B7B7',
    grey2: '#cecece',
    grey3: '#373737',
    knobColor: '#D9D9D9',
    pickerSelectBg: 'rgba(0,0,0,0.6)',
    invisible: 'rgba(0,0,0,0)',
    tmpGreen: '#2a6b35',
    tmpOrangeText: '#ffeeb9',
    tmpBlue: '#344186',
  },
  layout: {
    appPadding: 15,
    releaseItemGapHorizontal: 15,
    releaseItemGapVertical: 15,
    headerHeight: 63,
    borderRadius: 10,
    bottomNavHeight: 67,
    tabHorizontalPadding: 20,
    overviewTabHeight: 28,
    dialogWidth: 400,
  },
  name: 'dark' as ThemeName,
}
const ThemeMap: { [key in ThemeName]: Theme } = {
  dark: defaultTheme,
  light: {
    ...defaultTheme,
    color: {
      ...defaultTheme.color,
      background: '#ffffff',
      mainBackground: '#f5f5f5',
      secondaryBackground: '#eaeaea',
      modalBackground: '#e0e0e0',
      backdrop: 'rgba(255,255,255,0.7)',
      bg1: '#d3d3d3',
      buttonBg: '#d6d6d6',
      atsumeMain: '#d0549d',
      cardText: '#ca9898',
      errorColor: '#880a0a',
      dangerButtonBg: '#f5c6c6',
      textOnPublisherLabel: '#ffffff',
      mainText: '#1a1a1a',
      mainTextSlightlyDarker: '#4a4a4a',
      switchKnobColor: '#2D2D2D',
      switchKnobColorOff: '#a8a8a8',
      mainOnLight: '#1a1a1a',
      mainTextInactive: '#9d9d9d',
      grey1: '#6e6e6e',
      grey2: '#5a5a5a',
      grey3: '#c8c8c8',
      knobColor: '#414141',
      pickerSelectBg: 'rgba(255,255,255,0.6)',
      invisible: 'rgba(0,0,0,0)',
      tmpGreen: '#1d8a2a',
      tmpOrangeText: '#5c4200',
      tmpBlue: '#2a3580',
    },
    name: 'light' as ThemeName,
  },
}

export const THEME_NAMES = ['dark', 'light'] as const
export type ThemeName = (typeof THEME_NAMES)[number]
type Theme = typeof defaultTheme & { name: ThemeName }

function isDarkMode() {
  return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
}

function getContrastColor(backgroundColor: string): string {
  const hex = backgroundColor.replace('#', '')
  const r = parseInt(hex.substring(0, 2), 16)
  const g = parseInt(hex.substring(2, 4), 16)
  const b = parseInt(hex.substring(4, 6), 16)

  const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255
  return luminance > 0.66 ? '#000000' : '#ffffff'
}

export const useThemeStore = defineStore('theme', () => {
  const { theme, setTheme } = usePreferencesStore()
  const publishers = ref<Publisher[]>([])
  const publisherStyleElement = ref<HTMLStyleElement>()
  const getThemeNameFromPreference = (themePreference: ThemePreference): ThemeName => {
    return themePreference === 'automatic' ? (isDarkMode() ? 'dark' : 'light') : themePreference
  }

  const currentTheme = ref(ThemeMap[getThemeNameFromPreference(theme)])

  const selectTheme = async (themePreference: ThemePreference) => {
    const themeName = getThemeNameFromPreference(themePreference)
    currentTheme.value = ThemeMap[themeName]
    setCssVariables(currentTheme.value)
    updatePublisherStyles(publishers.value, currentTheme.value.name)
    if (Capacitor.isNativePlatform()) {
      try {
        await StatusBar.setStyle({ style: themeName === 'dark' ? Style.Dark : Style.Light })
      } catch (e) {
        console.error('StatusBar.setStyle failed:', e)
      }
    }
    await setTheme(themePreference)
  }

  const setCssVariables = (theme: Theme) => {
    for (const [key, value] of Object.entries(theme.color)) {
      document.documentElement.style.setProperty(
        `--${key.replace(/[A-Z]/g, (m) => '-' + m.toLowerCase())}`,
        value,
      )
    }
    for (const [key, value] of Object.entries(theme.layout)) {
      document.documentElement.style.setProperty(
        `--${key.replace(/[A-Z]/g, (m) => '-' + m.toLowerCase())}`,
        `${value}`,
      )
    }
  }

  function addStyleElement(css: string): HTMLStyleElement {
    const style = document.createElement('style')
    style.textContent = css
    document.head.appendChild(style)
    return style
  }

  function updatePublisherStyles(publishers: Publisher[], themeName: ThemeName) {
    if (publisherStyleElement.value) {
      publisherStyleElement.value.remove()
    }
    publisherStyleElement.value = addStyleElement(
      publishers
        .map((publisher) => {
          const pubClassName = publisherNameToCssClass(publisher.name!)
          const bgColor =
            themeName === 'dark' ? publisher.colorDarkTheme : publisher.colorLightTheme
          const fontColor = getContrastColor(bgColor!)
          return `.${pubClassName} { background-color: ${bgColor}; color: ${fontColor}; }`
        })
        .join('\n'),
    )
  }

  async function fetchPublisherStyles() {
    const result = await catalogQuery({
      query: GET_PUBLISHERS,
      variables: { orderBy: [], where: { disabled: { equals: false } } },
      fetchPolicy: 'network-only',
    })
    if (result?.data?.publishers) {
      publishers.value = result.data.publishers
      updatePublisherStyles(result.data.publishers, currentTheme.value.name)
    }
  }

  const init = async () => {
    document.body.classList.add(Capacitor.getPlatform())
    await selectTheme(getThemeNameFromPreference(theme))
    await fetchPublisherStyles()
  }

  return { currentTheme, init, selectTheme, fetchPublisherStyles }
})
